/**
 * Servicing payment-reminder window policy.
 *
 * Single source of truth for "how close must a payment be before we offer or
 * send a borrower reminder", consumed by:
 * - servicingService.getPaymentReminderCandidates (bulk candidate query cutoff)
 * - ServicingLoanDetail (per-loan Send Reminder enablement)
 * - send-servicing-payment-reminder (server-side enforcement)
 *
 * Import-free on purpose so Node/Vitest can import it from the Deno tree.
 * All dates are YYYY-MM-DD strings on the Pacific business-date basis.
 */

/** Inclusive look-ahead: payments due within this many calendar days qualify. */
export const REMINDER_WINDOW_DAYS = 20;

/**
 * Add whole calendar days to a YYYY-MM-DD string. UTC-tuple arithmetic — no
 * local-time Dates involved, so spring-forward/fall-back cannot shift the day.
 */
export function addCalendarDaysUtc(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/** Latest due date (inclusive) that qualifies for a reminder as of `todayISO`. */
export function reminderWindowCutoff(todayISO: string): string {
  return addCalendarDaysUtc(todayISO, REMINDER_WINDOW_DAYS);
}

/**
 * Whether a payment due on `dueDateISO` is remindable as of `todayISO`.
 * Overdue payments are always within the window.
 */
export function isWithinReminderWindow(dueDateISO: string, todayISO: string): boolean {
  return dueDateISO <= reminderWindowCutoff(todayISO);
}
